'use client';

import * as React from 'react';
import { useTranslations } from 'next-intl';
import { DiscountCode } from '@/lib/types/customer/restaurant/StoreProfile';
import { cn } from '@/lib/utils';
import {
  getClickIdForEvent,
  markEventAsTracked,
} from '@/lib/utils/adcashTracking';
import * as pixel from '@/lib/utils/fpixel';

import Button from '@ui/Button';
import Image from 'next/image';
import DiscountCodeModal from './DiscountCodeModal';

type DiscountCodeCardProps = {
  data: DiscountCode;
  onlineShopLink: string;
  storeName: string;
  isDisabled: boolean;
  handleOpenPhoneVerifyModal: () => void;
  storeLogo?: string;
};

const parseHashParams = (hash: string) => {
  const cleanHash = hash.startsWith('#') ? hash.slice(1) : hash;

  return cleanHash
    .split('&')
    .reduce<Record<string, string>>((acc, fragment) => {
      if (!fragment) return acc;

      const [rawKey, rawValue = ''] = fragment.split('=');
      try {
        const key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
        const value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
        acc[key] = value;
      } catch (error) {
        console.warn('Failed to parse hash fragment:', fragment, error);
      }

      return acc;
    }, {});
};

export default function DiscountCodeCard(props: DiscountCodeCardProps) {
  const {
    data,
    onlineShopLink,
    storeName,
    storeLogo,
    isDisabled,
    handleOpenPhoneVerifyModal,
  } = props;
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const t = useTranslations();
  const didOpenFromHash = React.useRef(false);

  React.useEffect(() => {
    const openFromHash = () => {
      const hashParams = parseHashParams(window.location.hash);
      const modalParam = hashParams['modal'];
      const codeParam = hashParams['code'];

      if (modalParam === 'discount' && codeParam === data.code) {
        didOpenFromHash.current = true;
        setIsModalOpen(true);
      }
    };

    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, [data.code]);

  React.useEffect(() => {
    if (!isModalOpen && didOpenFromHash.current) {
      const hashParams = parseHashParams(window.location.hash);
      if (
        hashParams['modal'] === 'discount' &&
        hashParams['code'] === data.code
      ) {
        const newUrl = window.location.pathname + window.location.search;
        window.history.replaceState({}, '', newUrl);
      }
      didOpenFromHash.current = false;
    }
  }, [data.code, isModalOpen]);

  const handleOpenModal = () => {
    if (isDisabled) {
      handleOpenPhoneVerifyModal();
      return;
    }

    // Facebook Pixel tracking for discount code click
    pixel.customEvent('DiscountCodeClick', {
      code: data.code,
      store: storeName,
    });

    // Adcash tracking (fire and forget) - only track if not already tracked
    const adcashClickId = getClickIdForEvent('button_click');
    if (adcashClickId) {
      const payload = JSON.stringify({
        clickId: adcashClickId,
        buttonContext: 'discount_card_open',
        variable: 'DiscountCodeButton',
        metadata: { code: data.code, store: storeName },
      });

      // Use sendBeacon for non-blocking tracking before navigation
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        const sent = navigator.sendBeacon('/api/adcash/button-click', blob);
        if (sent) {
          markEventAsTracked('button_click');
        }
      } else {
        // Fallback to fetch with keepalive
        fetch('/api/adcash/button-click', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        })
          .then(() => markEventAsTracked('button_click'))
          .catch((err) =>
            console.error('[Adcash] Button tracking failed:', err)
          );
      }
    }

    if (onlineShopLink) {
      // 1. Open our current page with modal auto-opened in new tab
      // 2. Redirect current tab to shop link

      const url = new URL(window.location.href);

      // Remove any existing hash/query params we don't want to carry over
      url.searchParams.delete('modal');
      url.searchParams.delete('code');
      url.hash = `modal=discount&code=${encodeURIComponent(data.code)}`;
      const urlWithModal = url.toString();

      const newTab = window.open(urlWithModal, '_blank', 'noopener,noreferrer');
      if (!newTab) {
        console.warn(
          'Unable to open discount modal tab, continuing in current tab.'
        );
      }

      window.location.href = onlineShopLink;
    } else {
      // Fallback: just open modal if no shop link
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div
        role="button"
        onClick={handleOpenModal}
        className={cn(
          'flex cursor-pointer flex-col space-y-3 rounded-2xl bg-white px-5 py-5 shadow-[0px_1px_5px_0px_#0000001C] sm:flex-row sm:items-center sm:justify-between sm:space-y-0'
        )}
      >
        <div className="flex items-center space-x-4 sm:space-x-3">
          <Image
            src={'/discount-bank.png'}
            alt="discount-code-icon"
            width={100}
            height={100}
          />

          <div className="sm:pl-8 sm:pr-4">
            <h3 className="text-h4 font-bold text-paragraph">
              {/* {data.cashback} */}
              {data.cashback} {t('common.discount_code')}
            </h3>
            {data && data.description && (
              <div
                className="w-full min-w-full text-h8 font-normal leading-[150%] text-paragraph"
                dangerouslySetInnerHTML={{
                  __html: data.description,
                }}
              />
            )}
          </div>
        </div>

        <div className="relative flex flex-shrink-0 flex-col">
          <div className="flex items-center justify-end text-sm font-semibold">
            <Button
              type="button"
              rounded
              className={cn(
                'bg-primary-800 py-4 transition-colors duration-200 hover:bg-primary-900',
                isDisabled && 'pointer-events-none opacity-60'
              )}
              disabled={isDisabled}
            >
              {t('storefront.store_profile.copy_code')}
            </Button>
          </div>
        </div>
      </div>

      <DiscountCodeModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        data={data}
        storeName={storeName}
        onlineShopLink={onlineShopLink}
        storeLogo={storeLogo}
      />
    </>
  );
}
